// We use the browser's built-in API for rendering country names, instead of fetching them from Ingrid's CDN.
// Since we don't have the same requirement for backwards compatability for backoffice apps. It has been supported in all browsers since 2020-2021.
import { COUNTRY_CODES } from './country-codes';
import { useTranslation } from './use-translation';

class CountryNames {
  private static _instance?: Intl.DisplayNames;

  public static instance(locale: string) {
    if (this._instance?.resolvedOptions().locale !== locale) {
      this._instance = new Intl.DisplayNames([locale], { type: 'region' });
    }
    return this._instance;
  }

  public static getCountries = (locale: string) =>
    COUNTRY_CODES.map((code): [string, string] => [code, CountryNames.instance(locale).of(code)!]).sort((a, b) =>
      a[1].localeCompare(b[1], locale)
    );
}

export function useLocalizeCountry() {
  const { i18n } = useTranslation();
  return (code: string) => {
    try {
      if (code.length > 2) {
        return code;
      }

      return CountryNames.instance(i18n.resolvedLanguage!).of(code);
    } catch (e) {
      console.error(e);
      return;
    }
  };
}

interface Props {
  code: string;
}

export function LocalizedCountry({ code }: Props) {
  const localize = useLocalizeCountry();
  if (code.length > 2) {
    return code;
  }
  return <>{localize(code)}</>;
}

export const useLocalizedCountryList = () => {
  const { i18n } = useTranslation();
  return CountryNames.getCountries(i18n.resolvedLanguage!);
};
